// Utility functions for parsing structured AI feedback

/**
 * Strips common markdown formatting from AI response text so it renders
 * cleanly as plain text in MUI Typography components.
 */
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')       // **bold** → bold
    .replace(/\*(.+?)\*/g, '$1')            // *italic* → italic
    .replace(/__(.+?)__/g, '$1')            // __bold__ → bold
    .replace(/_(.+?)_/g, '$1')              // _italic_ → italic
    .replace(/^#{1,6}\s+/gm, '')            // ### heading → heading
    .replace(/^[-*+]\s+/gm, '• ')           // - item → • item (keep as bullet)
    .replace(/^\d+\.\s+/gm, '')             // 1. item → item
    .replace(/`([^`]+)`/g, '$1')            // `code` → code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [text](url) → text
    .replace(/\n{3,}/g, '\n\n')             // collapse excessive newlines
    .trim();
}

export interface ScoreResult {
  type: 'numeric' | 'text';
  numericValue?: number;
  textValue?: string;
}

export interface CriteriaFeedback {
  name: string;
  weight: number;
  score: number;
  aiScore: ScoreResult;
  manualScore?: number;
  finalScore: number;
  isOverridden: boolean;
  feedback: string;
  strengths?: string;
  improvements?: string;
}

export interface CategoryFeedback {
  category: string;
  displayName: string;
  score?: number;
  totalWeight?: number;
  displayValue?: string;
  /** When the category was last scored against the call's CAD record (cad-records leg 6); absent for an audio-only score. */
  cadScoredAt?: string;
  criteria: CriteriaFeedback[];
  strengths: string[];
  improvements: string[];
  reasoning: string[];
  rawFeedback: string;
}

export interface GlobalFeedback {
  strengths: string;
  improvements: string;
}

export function parseEnhancedDetailedScores(detailedScoresJson?: string): {
  categories: CategoryFeedback[];
  globalFeedback: GlobalFeedback;
} {
  if (!detailedScoresJson) {
    return { categories: [], globalFeedback: { strengths: '', improvements: '' } };
  }

  try {
    const enhancedScores = JSON.parse(detailedScoresJson);

    // Extract global feedback — strip markdown so it renders as clean plain text
    const globalFeedback: GlobalFeedback = {
      strengths: stripMarkdown(enhancedScores.GlobalStrengths || ''),
      improvements: stripMarkdown(enhancedScores.GlobalImprovements || ''),
    };

    const categories: CategoryFeedback[] = [];
    const categoriesData = enhancedScores.Categories || enhancedScores;

    for (const [categoryKey, categoryData] of Object.entries(categoriesData)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = categoryData as any;

      // Parse the new backend format
      const criteriaArray = data.Criteria || data.criteria || [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const criteria: CriteriaFeedback[] = criteriaArray.map((criterion: any) => {
        // Parse the new AiScore format from pipe-delimited parsing
        const aiScoreObj = criterion.AiScore || criterion.aiScore;
        let aiScore: ScoreResult;

        if (aiScoreObj && typeof aiScoreObj === 'object') {
          // Handle the new ScoreResult structure
          const isNumeric = aiScoreObj.IsNumeric ?? aiScoreObj.isNumeric ?? false;
          const numericValue = aiScoreObj.NumericValue ?? aiScoreObj.numericValue;
          const textValue = aiScoreObj.TextValue ?? aiScoreObj.textValue;

          if (isNumeric && numericValue !== undefined) {
            aiScore = {
              type: 'numeric',
              numericValue: numericValue,
            };
          } else {
            aiScore = {
              type: 'text',
              textValue: textValue || '',
            };
          }
        } else {
          // Fallback for simple values
          if (typeof aiScoreObj === 'number') {
            aiScore = { type: 'numeric', numericValue: aiScoreObj };
          } else {
            aiScore = { type: 'text', textValue: String(aiScoreObj || '') };
          }
        }

        return {
          name: criterion.Name || criterion.name || '',
          weight: criterion.Weight || criterion.weight || 0,
          score: criterion.FinalScore || criterion.finalScore || 0,
          aiScore: aiScore,
          manualScore: criterion.ManualScore || criterion.manualScore,
          finalScore: criterion.FinalScore || criterion.finalScore || 0,
          isOverridden: criterion.IsOverridden || criterion.isOverridden || false,
          feedback: stripMarkdown(criterion.Feedback || criterion.feedback || ''),
          strengths: '', // No longer duplicated - use global feedback
          improvements: '', // No longer duplicated - use global feedback
        };
      });

      categories.push({
        category: categoryKey,
        displayName: categoryKey,
        score: data.CategoryScore || data.categoryScore,
        totalWeight: data.CategoryWeight || data.categoryWeight,
        displayValue: data.DisplayValue || data.displayValue,
        cadScoredAt: data.CadScoredAt || data.cadScoredAt,
        criteria,
        strengths: [], // Use global feedback instead
        improvements: [], // Use global feedback instead
        reasoning: [],
        rawFeedback: '',
      });
    }

    return { categories, globalFeedback };
  } catch (_error) {
    return { categories: [], globalFeedback: { strengths: '', improvements: '' } };
  }
}

export function parseAIAnalysisFeedback(
  _comprehensiveFeedback?: string,
  detailedScoresJson?: string
): { categories: CategoryFeedback[]; globalFeedback: GlobalFeedback } {
  // Parse the new enhanced format with global feedback
  const enhancedResults = parseEnhancedDetailedScores(detailedScoresJson);

  if (enhancedResults.categories.length === 0) {
    // No enhanced detailed scores found
  }

  return enhancedResults;
}

export function getCategoryFeedback(
  _comprehensiveFeedback?: string,
  categoryKey?: string,
  detailedScoresJson?: string
): CategoryFeedback | null {
  const { categories } = parseAIAnalysisFeedback(_comprehensiveFeedback, detailedScoresJson);
  return categories.find((f) => f.category === categoryKey) || null;
}
